<template>
  <div
    ref="container"
    class="flex h-full relative"
    :class="[
      direction === 'vertical' ? 'flex-col' : 'flex-row',
      isDragging ? 'select-none' : '',
      isDragging && direction === 'vertical' ? 'cursor-row-resize' : '',
      isDragging && direction === 'horizontal' ? 'cursor-col-resize' : '',
    ]"
  >
    <!-- First pane -->
    <div
      :style="fixedPane === 'first' ? fixedPaneStyle : undefined"
      :class="fixedPane === 'first' ? 'shrink-0 overflow-hidden' : 'flex-1 overflow-hidden min-w-0 min-h-0'"
    >
      <slot name="first" />
    </div>

    <!-- Drag handle -->
    <div
      class="shrink-0 bg-gray-700 hover:bg-blue-500 active:bg-blue-500 transition-colors z-10"
      :class="direction === 'vertical'
        ? 'h-1 w-full cursor-row-resize'
        : 'w-1 h-full cursor-col-resize'"
      @mousedown.prevent="startDrag"
    />

    <!-- Second pane -->
    <div
      :style="fixedPane === 'second' ? fixedPaneStyle : undefined"
      :class="fixedPane === 'second' ? 'shrink-0 overflow-hidden' : 'flex-1 overflow-hidden min-w-0 min-h-0'"
    >
      <slot name="second" />
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onUnmounted } from 'vue'

const props = withDefaults(
  defineProps<{
    direction?:  'horizontal' | 'vertical'
    initialSize?: number
    minFirst?:   number
    minSecond?:  number
    /** Which pane has a fixed pixel size; the other takes flex-1. Default: 'first'. */
    fixedPane?:  'first' | 'second'
    /** Maximum size (px) the fixed pane may reach when dragging. */
    maxFixed?:   number
  }>(),
  { direction: 'horizontal', initialSize: 300, minFirst: 80, minSecond: 80, fixedPane: 'first' },
)

const emit = defineEmits<{ (e: 'resize', size: number): void }>()

const container  = ref<HTMLElement>()
const fixedSize  = ref(props.initialSize)
const isDragging = ref(false)

let cleanupMove: ((e: MouseEvent) => void) | null = null
let cleanupUp:   (() => void) | null = null

const fixedPaneStyle = computed(() => ({
  [props.direction === 'vertical' ? 'height' : 'width']: `${fixedSize.value}px`,
}))

function startDrag(e: MouseEvent): void {
  isDragging.value = true
  const startPos  = props.direction === 'vertical' ? e.clientY : e.clientX
  const startSize = fixedSize.value

  const onMove = (ev: MouseEvent): void => {
    const delta         = (props.direction === 'vertical' ? ev.clientY : ev.clientX) - startPos
    const containerSize = props.direction === 'vertical'
      ? (container.value?.offsetHeight ?? 0)
      : (container.value?.offsetWidth  ?? 0)

    if (props.fixedPane === 'first') {
      const maxSize = props.maxFixed ?? (containerSize - props.minSecond)
      fixedSize.value = Math.max(
        props.minFirst,
        Math.min(startSize + delta, maxSize, containerSize - props.minSecond),
      )
    } else {
      // fixedPane === 'second': dragging the handle upward grows the second pane
      const maxSize = props.maxFixed ?? (containerSize - props.minFirst)
      fixedSize.value = Math.max(
        props.minSecond,
        Math.min(startSize - delta, maxSize, containerSize - props.minFirst),
      )
    }
  }

  const onUp = (): void => {
    isDragging.value = false
    emit('resize', fixedSize.value)
    window.removeEventListener('mousemove', onMove)
    window.removeEventListener('mouseup', onUp)
    cleanupMove = null
    cleanupUp   = null
  }

  cleanupMove = onMove
  cleanupUp   = onUp
  window.addEventListener('mousemove', onMove)
  window.addEventListener('mouseup', onUp)
}

onUnmounted(() => {
  if (cleanupMove) window.removeEventListener('mousemove', cleanupMove)
  if (cleanupUp)   window.removeEventListener('mouseup', cleanupUp)
})
</script>
